/**
 * Customers Page - Notion-Inspired Design
 * View and manage loyalty program customers
 */

import React, { useEffect, useState, useCallback } from 'react';
import { customersApi, Customer } from '../lib/api';
import { toast } from '../components/ui';
import { Button, Input, Card, TableContainer, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TablePagination, TableSkeleton, EmptyState, NoCustomersIllustration } from '../components/ui';
import { debounce } from '../utils/helpers';

type CustomerSegment = 'all' | 'vips' | 'at-risk' | 'new' | 'inactive';

export const CustomersPage: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [segment, setSegment] = useState<CustomerSegment>('all');
  const [sortBy, setSortBy] = useState<'createdAt' | 'lifetimeEarned' | 'b3trBalance'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setDebouncedSearchTerm(value);
      setPage(1); // Reset to page 1 on new search
    }, 500),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    debouncedSearch(e.target.value);
  };

  useEffect(() => {
    loadCustomers();
  }, [debouncedSearchTerm, sortBy, sortOrder, page, segment]);

  const loadCustomers = async () => {
    setIsLoading(true);
    try {
      const response = await customersApi.getAll({
        search: debouncedSearchTerm || undefined,
        sortBy,
        sortOrder,
        page,
        limit,
      });
      const allCustomers = response.data.data;
      const now = Date.now();
      const filtered = allCustomers.filter((c) => {
        if (segment === 'all') return true;
        const lastTx = c.updatedAt ? new Date(c.updatedAt).getTime() : new Date(c.createdAt).getTime();
        const daysSince = (now - lastTx) / (1000 * 60 * 60 * 24);
        const tier = (c as any).tier as string | undefined;
        if (segment === 'vips') return tier === 'GOLD' || tier === 'PLATINUM';
        if (segment === 'at-risk') return daysSince >= 30 && daysSince < 90;
        if (segment === 'new') {
          const joinedDaysAgo = (now - new Date(c.createdAt).getTime()) / (1000 * 60 * 60 * 24);
          return joinedDaysAgo <= 7;
        }
        if (segment === 'inactive') return daysSince >= 90;
        return true;
      });
      setCustomers(filtered);
      setTotal(response.data.meta.total);
    } catch (error) {
      console.error('Failed to load customers:', error);
      toast.error('Failed to load customers. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handleExportCSV = () => {
    if (customers.length === 0) {
      toast.error('No customers to export.');
      return;
    }
    const headers = ['First Name', 'Last Name', 'Email', 'Stars Balance', 'Lifetime Earned', 'Lifetime Spent', 'Joined'];
    const rows = customers.map((c) => [
      c.firstName ?? '',
      c.lastName ?? '',
      c.email ?? '',
      parseFloat(c.b3trBalance.toString()).toFixed(2),
      parseFloat(c.lifetimeEarned.toString()).toFixed(2),
      parseFloat(c.lifetimeSpent.toString()).toFixed(2),
      new Date(c.createdAt).toLocaleDateString(),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-h1 font-bold text-black">
            Customers
          </h1>
          <p className="text-base mt-2 text-black-lighter">
            {total} total customers in your loyalty program
          </p>
        </div>
        <Button variant="secondary" size="md" onClick={handleExportCSV} className="whitespace-nowrap self-start sm:self-auto">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Export CSV
        </Button>
      </div>

      {/* Segment Pills */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { id: 'all' as const, label: 'All Customers', icon: '👥' },
          { id: 'vips' as const, label: 'VIPs (Buffies VIP+)', icon: '⭐' },
          { id: 'at-risk' as const, label: 'At Risk (30+ days)', icon: '⚠️' },
          { id: 'new' as const, label: 'New (Last 7 days)', icon: '✨' },
          { id: 'inactive' as const, label: 'Inactive (90+ days)', icon: '💤' },
        ].map((seg) => (
          <button
            key={seg.id}
            onClick={() => {
              setSegment(seg.id);
              setPage(1);
            }}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              segment === seg.id
                ? 'bg-orange-400 text-white shadow-sm'
                : 'bg-white text-black-lighter border border-border-light hover:border-orange-200 hover:text-black'
            }`}
          >
            <span>{seg.icon}</span>
            {seg.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <div className="p-5">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex-1">
              <Input
                type="search"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
            <Input
              type="select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="sm:min-w-50"
            >
              <option value="createdAt">Recently Joined</option>
              <option value="lifetimeEarned">Most Earned</option>
              <option value="b3trBalance">Highest Balance</option>
            </Input>
          </div>
        </div>
      </Card>

      {/* Customers Table */}
      {isLoading ? (
        <Card>
          <div className="p-5">
            <TableSkeleton rows={10} columns={6} />
          </div>
        </Card>
      ) : customers.length === 0 ? (
        <EmptyState
          illustration={<NoCustomersIllustration />}
          title="No customers yet"
          description="Customers will appear here once they join your loyalty program"
        />
      ) : (
        <TableContainer>
          <Table>
            <TableHeader>
              <tr>
                <TableHead>Customer</TableHead>
                <TableHead sortable onClick={() => handleSort('b3trBalance')}>
                  <div className="flex items-center gap-1">
                    Stars Balance
                    {sortBy === 'b3trBalance' && (
                      <span className="text-orange-400">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </TableHead>
                <TableHead sortable onClick={() => handleSort('lifetimeEarned')}>
                  <div className="flex items-center gap-1">
                    Lifetime Earned
                    {sortBy === 'lifetimeEarned' && (
                      <span className="text-orange-400">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </TableHead>
                <TableHead>Lifetime Spent</TableHead>
                <TableHead sortable onClick={() => handleSort('createdAt')}>
                  <div className="flex items-center gap-1">
                    Joined
                    {sortBy === 'createdAt' && (
                      <span className="text-orange-400">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </tr>
            </TableHeader>
            <TableBody>
              {customers.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm bg-orange-100 text-orange-600">
                        {customer.firstName?.charAt(0)}{customer.lastName?.charAt(0)}
                      </div>
                      <div>
                        <p className="text-base font-medium text-black">
                          {customer.firstName} {customer.lastName}
                        </p>
                        <p className="text-xs text-black-lighter">{customer.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-base font-medium text-black">
                      {parseFloat(customer.b3trBalance.toString()).toLocaleString()} Stars
                    </p>
                  </TableCell>
                  <TableCell>
                    <p className="text-base text-black-light">
                      {parseFloat(customer.lifetimeEarned.toString()).toLocaleString()} Stars
                    </p>
                  </TableCell>
                  <TableCell>
                    <p className="text-base text-black-light">
                      {parseFloat(customer.lifetimeSpent.toString()).toLocaleString()} Stars
                    </p>
                  </TableCell>
                  <TableCell>
                    <p className="text-xs text-black-lighter">
                      {new Date(customer.createdAt).toLocaleDateString()}
                    </p>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="tertiary" size="sm">
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <TablePagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={total}
              itemsPerPage={limit}
            />
          )}
        </TableContainer>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0 bg-orange-100 text-orange-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase mb-2 text-black-lighter tracking-wide">
                  Top Customer
                </p>
                <p className="text-h4 font-semibold truncate text-black">
                  {customers[0]
                    ? `${customers[0].firstName} ${customers[0].lastName}`
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0 bg-success-100 text-success-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase mb-2 text-black-lighter tracking-wide">
                  Avg. Balance
                </p>
                <p className="text-h4 font-semibold truncate text-black">
                  {customers.length > 0
                    ? (
                        customers.reduce((sum, c) => sum + parseFloat(c.b3trBalance.toString()), 0) /
                        customers.length
                      ).toFixed(0)
                    : '0'}{' '}
                  Stars
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0 bg-blue-100 text-blue-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase mb-2 text-black-lighter tracking-wide">
                  Total Earned
                </p>
                <p className="text-h4 font-semibold truncate text-black">
                  {customers
                    .reduce((sum, c) => sum + parseFloat(c.lifetimeEarned.toString()), 0)
                    .toLocaleString()}{' '}
                  Stars
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
